import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const NAV_LINKS = [
  { label: 'Home',      path: '/' },
  { label: 'Products',  path: '/products' },
  { label: 'About',     path: '/about' },
  { label: 'Knowledge', path: '/knowledge' },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled,  setScrolled]  = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  const goTo = (path) => { navigate(path); setMenuOpen(false); window.scrollTo(0, 0); };

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-white/96 backdrop-blur-md shadow-sm border-b border-steel-200'
          : 'bg-white border-b border-steel-100'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">

        {/* ── Logo: real brand image + wordmark ── */}
        <button
          data-testid="nav-logo"
          onClick={() => goTo('/')}
          className="flex items-center gap-2.5 group"
          aria-label="Apex Precitech — home"
        >
          {/* Real logo mark — WebP with PNG fallback, transparent bg, 2× retina */}
          <picture>
            <source
              type="image/webp"
              srcSet="/images/apex-mark-sm.webp 1x, /images/apex-mark.webp 2x"
            />
            <img
              src="/images/apex-mark.png"
              alt="Apex mountain mark"
              width={28}
              height={40}
              loading="eager"
              decoding="sync"
              style={{ width: 'auto', height: '40px', display: 'block' }}
            />
          </picture>

          {/* Wordmark */}
          <div className="flex items-baseline leading-none gap-1">
            <span
              className="font-heading font-bold text-xl tracking-wide uppercase"
              style={{ color: '#CC1E14', letterSpacing: '0.04em' }}
            >
              APEX
            </span>
            <span
              className="font-heading font-normal text-sm uppercase tracking-widest"
              style={{ color: '#94A3B8', letterSpacing: '0.12em' }}
            >
              PRECITECH
            </span>
          </div>
        </button>

        {/* ── Desktop nav links ── */}
        <div className="hidden md:flex items-center gap-0.5">
          {NAV_LINKS.map(link => (
            <button
              key={link.path}
              onClick={() => goTo(link.path)}
              className={`font-mono text-[11px] uppercase tracking-wider px-4 py-2 transition-all duration-200 border-b-2 ${
                isActive(link.path)
                  ? 'text-steel-900 border-safety'
                  : 'text-steel-500 border-transparent hover:text-steel-900 hover:border-steel-300'
              }`}
            >
              {link.label}
            </button>
          ))}
          <button
            data-testid="nav-get-quote"
            onClick={() => window.open(
              'https://wa.me/919843035014?text=Hi%20Apex%20Precitech%2C%20I%20would%20like%20to%20get%20a%20quote.',
              '_blank'
            )}
            className="ml-4 bg-safety text-white font-heading uppercase text-[11px] tracking-wider px-5 py-2.5 hover:-translate-y-0.5 hover:bg-orange-600 transition-all duration-200"
          >
            Get Quote
          </button>
        </div>

        {/* ── Mobile toggle ── */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden p-2 text-steel-900"
          aria-label="Toggle menu"
        >
          {menuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* ── Mobile menu ── */}
      {menuOpen && (
        <div className="md:hidden bg-white border-b border-steel-200">
          <div className="px-6 py-3 space-y-0.5">
            {NAV_LINKS.map(link => (
              <button
                key={link.path}
                onClick={() => goTo(link.path)}
                className={`block w-full text-left font-mono text-xs uppercase tracking-wider py-3 px-2 border-b border-steel-100 transition-colors ${
                  isActive(link.path) ? 'text-safety font-semibold' : 'text-steel-600 hover:text-steel-900'
                }`}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => window.open(
                'https://wa.me/919843035014?text=Hi%20Apex%20Precitech%2C%20I%20would%20like%20to%20get%20a%20quote.',
                '_blank'
              )}
              className="w-full bg-safety text-white font-heading uppercase text-xs tracking-wider py-3 mt-3"
            >
              Get Quote on WhatsApp
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
